import { Benefit, BenefitDetail, BenefitsAPIResponse, BenefitsQueryData } from '../types';

export const BENEFITS_PAGE_SIZE = 50;

/** Maps the API page to the cache shape; `page` is the requested page (1-based). */
export const normalizeBenefitsResponse = (
  response: BenefitsAPIResponse,
  page: number,
): BenefitsQueryData => ({
  benefits: response.benefits.map(({ uuid, name, ...benefit }) => ({
    id: uuid,
    title: name,
    thumbnailImageUrl: benefit.thumbnailImageUrl,
    largerImageUrl: benefit.largerImageUrl,
    description: benefit.description,
    link: benefit.link,
    vendorPhone: benefit.vendorPhone,
    vendorName: benefit.vendorName,
  })),
  totalPages: response.totalPages,
  lastLoadedPage: page,
});

/** Replace on page 1, append otherwise (skipping benefits already in the cache). */
export const mergeBenefitsPages = (
  currentCache: BenefitsQueryData,
  newData: BenefitsQueryData,
  page: number,
): BenefitsQueryData => {
  if (page === 1) {
    return newData;
  }
  const knownIds = new Set(currentCache.benefits.map(({ id }) => id));
  return {
    totalPages: newData.totalPages,
    lastLoadedPage: newData.lastLoadedPage,
    benefits: [...currentCache.benefits, ...newData.benefits.filter(({ id }) => !knownIds.has(id))],
  };
};

/** Maps a list benefit onto the shape the detail modal renders. */
export const toBenefitDetail = (benefit: Benefit): BenefitDetail => ({
  title: benefit.title,
  description: benefit.description,
  imageUrl: benefit.largerImageUrl,
  phoneNumber: benefit.vendorPhone ?? undefined,
  visitUrl: benefit.link ?? undefined,
});

export const MAX_PREFETCHED_IMAGES = 50;

// Starts loading an image ahead of time so the browser's HTTP cache is likely warm when it is
// displayed
export const createImagePrefetcher = (
  maxEntries = MAX_PREFETCHED_IMAGES,
  createImage: () => HTMLImageElement = () => new Image(),
) => {
  const prefetched = new Map<string, HTMLImageElement>();

  return (url?: string) => {
    if (!url || prefetched.has(url)) {
      return;
    }
    if (prefetched.size >= maxEntries) {
      const oldestUrl = prefetched.keys().next().value;
      if (oldestUrl !== undefined) {
        prefetched.delete(oldestUrl);
      }
    }
    const image = createImage();
    image.src = url;
    prefetched.set(url, image);
  };
};
